// src/components/FavoritesButton.js
import React, { useState } from 'react';
import { Heart, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const FavoritesButton = ({ 
  product, 
  size = 'medium',
  showLabel = true,
  className = '' 
}) => {
  const { isAuthenticated, addToFavorites, removeFromFavorites, isFavorite } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const isProductFavorite = isFavorite(product.id);

  const handleFavoriteClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // אם המשתמש לא מחובר, הפנה להתחברות
    if (!isAuthenticated) {
      navigate('/auth/login', { 
        state: { 
          from: { pathname: window.location.pathname },
          message: 'התחבר כדי לשמור מוצרים במועדפים' 
        }
      });
      return;
    }

    setLoading(true);

    try {
      if (isProductFavorite) {
        const result = await removeFromFavorites(product.id);
        if (!result.success) {
          console.error('Failed to remove from favorites:', result.error);
        }
      } else {
        const productData = {
          name: product.name,
          company: product.company,
          category: product.category || product.source
        };
        const result = await addToFavorites(product.id, productData);
        if (!result.success) {
          console.error('Failed to add to favorites:', result.error);
        }
      }
    } catch (error) {
      console.error('Error handling favorite:', error);
    } finally {
      setLoading(false);
    }
  };

  // גדלים שונים
  const sizes = {
    small: {
      button: 'p-2',
      icon: 'w-4 h-4',
      text: 'text-xs'
    },
    medium: {
      button: 'p-3',
      icon: 'w-5 h-5',
      text: 'text-sm'
    },
    large: {
      button: 'p-4',
      icon: 'w-6 h-6',
      text: 'text-base'
    }
  };

  const currentSize = sizes[size] || sizes.medium;

  return (
    <button
      onClick={handleFavoriteClick}
      disabled={loading}
      className={`
        relative flex items-center gap-2 rounded-xl font-medium transition-all duration-300
        transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed
        ${currentSize.button} ${currentSize.text}
     ${isAuthenticated
  ? isProductFavorite
    ? 'bg-red-100 hover:bg-red-200 text-red-700 border-2 border-red-300'
    : 'bg-white/90 hover:bg-gray-50 text-gray-600 border border-gray-200 hover:border-red-300'
  : 'bg-white/90 hover:bg-gray-50 text-gray-600 border border-gray-200 hover:border-blue-300'
}
        ${className}
      `}
      title={
        !isAuthenticated 
          ? 'התחבר כדי לשמור במועדפים'
          : isProductFavorite 
            ? 'הסר מהמועדפים' 
            : 'הוסף למועדפים'
      }
    >
      {/* אפקט זוהר בהובר */}
      <div className="absolute inset-0 bg-gradient-to-r from-red-400/10 to-pink-400/10 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
      
      <span className="relative z-10 flex items-center gap-2">
        {!isAuthenticated ? (
          <Heart className={`${currentSize.icon} transition-transform duration-300`} />
        ) : (
          <Heart 
            className={`
              ${currentSize.icon} transition-all duration-300
              ${isProductFavorite ? 'fill-current text-red-500' : ''}
              ${loading ? 'animate-pulse' : ''}
            `} 
          />
        )}
        
        {showLabel && (
          <span className="font-semibold ">
            {!isAuthenticated 
              ? 'הוסף למועדפים'
              : loading
                ? '...'
                : isProductFavorite 
                  ? 'במועדפים' 
                  : 'הוסף למועדפים'
            }
          </span>
        )}
      </span>

      {/* אנימציה של לבבות קטנים */}
      {isProductFavorite && !loading && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1 right-1 w-2 h-2 bg-red-400 rounded-full animate-ping opacity-30"></div>
          <div className="absolute bottom-1 left-1 w-1.5 h-1.5 bg-pink-400 rounded-full animate-ping opacity-40 animation-delay-200"></div>
        </div>
      )}
    </button>
  );
};

// רכיב לב פשוט יותר לשימוש בטבלאות
export const SimpleFavoriteButton = ({ product, className = '' }) => {
  const { isAuthenticated, addToFavorites, removeFromFavorites, isFavorite } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const isProductFavorite = isFavorite(product.id);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/auth/login');
      return;
    }

    setLoading(true);

    try {
      if (isProductFavorite) {
        await removeFromFavorites(product.id);
      } else {
        const productData = {
          name: product.name,
          company: product.company,
          category: product.category || product.source
        };
        await addToFavorites(product.id, productData);
      }
    } catch (error) {
      console.error('Error handling favorite:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className={`
        p-2 rounded-lg transition-all duration-200 hover:scale-110 disabled:opacity-50
        ${isAuthenticated
          ? isProductFavorite
            ? 'text-red-500 hover:bg-red-50'
            : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
          : 'text-blue-500 hover:bg-blue-50'
        }
        ${className}
      `}
      title={
        !isAuthenticated 
          ? 'התחבר כדי לשמור'
          : isProductFavorite 
            ? 'הסר מהמועדפים' 
            : 'הוסף למועדפים'
      }
    >
      {!isAuthenticated ? (
        <LogIn className="w-4 h-4" />
      ) : (
        <Heart 
          className={`
            w-4 h-4 transition-all duration-200
            ${isProductFavorite ? 'fill-current' : ''}
            ${loading ? 'animate-pulse' : ''}
          `} 
        />
      )}
    </button>
  );
};

export default FavoritesButton;